import { formatDate, formatNumber, toNumber } from '@/utils/format';
import { amountToVietnameseWords } from '@/utils/amountToVietnameseWords';
import type { TenantOrder } from '@/mocks/orders';

/**
 * TCR-1962 — tài liệu "ĐƠN HÀNG" gửi khách (xác nhận đơn, có người nhận / địa
 * chỉ giao) theo mẫu QA đính kèm. Khác "In hoá đơn bán hàng" (chứng từ VAT, mẫu
 * lưu ở BE `receipt-template`): tài liệu này dựng hoàn toàn ở FE để xuất
 * PDF / Ảnh / Word — xem `exportOrderCustomerDoc.ts`.
 *
 * File này chỉ gồm hàm THUẦN (dữ liệu → số liệu → HTML) để test không cần DOM.
 *
 * TCR-2506: bố cục bám theo file Word mẫu khách đưa (khung "ĐƠN HÀNG" của
 * đơn sỉ GT/MT) — cả bản HTML ở đây lẫn bản Word (`orderCustomerDocx.ts`)
 * phải khớp mẫu để in ra và tải về cho khách giống nhau.
 */

export interface OrderCustomerDocLine {
  name: string;
  unit: string;
  qty: number;
  unitPrice: number;
  amount: number;
}

export interface OrderCustomerDocData {
  orderNumber: string;
  orderDate: string;
  customerName: string;
  taxCode: string;
  billingAddress: string;
  recipientName: string;
  recipientPhone: string;
  shippingAddress: string;
  /** Ghi chú giao hàng (shipping.address.note) — rỗng thì phiếu không in dòng này. */
  deliveryNote: string;
  lines: OrderCustomerDocLine[];
  /** Tổng các dòng trên bảng (hàng + phụ thu + phí ship khách trả), CHƯA VAT. */
  subtotal: number;
  discount: number;
  vat: number;
  total: number;
}

type ServiceLine = NonNullable<TenantOrder['service_lines']>[number];

/** `unit_price` legacy — cùng lý do `ReceiptLineItem` ở `printReceipt.ts`. */
type DocItem = NonNullable<TenantOrder['items']>[number] & { unit_price?: number; name?: string };

/** Đơn giá GỐC trước chiết khấu dòng — giống `itemGrossPrice` của hoá đơn bán
 *  hàng, để tổng các dòng khớp `summary.original_price` và chiết khấu hiện riêng. */
function grossUnitPrice(it: DocItem): number {
  const original = toNumber(it.original_price);
  if (original > 0) return original;
  return toNumber(it.price ?? it.unit_price);
}

/** Nối các phần địa chỉ, bỏ phần rỗng và phần đã nằm sẵn trong chuỗi phía trước
 *  (địa chỉ hoá đơn khách nhập tay thường đã kèm phường/tỉnh). */
export function joinAddress(parts: Array<string | undefined | null>): string {
  const out: string[] = [];
  for (const raw of parts) {
    const part = raw?.trim();
    if (!part) continue;
    if (out.join(', ').toLowerCase().includes(part.toLowerCase())) continue;
    out.push(part);
  }
  return out.join(', ');
}

/**
 * Phí vận chuyển KHÁCH trả, tách VAT nếu có.
 *
 * `summary.total` KHÔNG bao giờ gồm phí ship (đã đối chiếu DB dev: đơn tự VC lẫn
 * ĐVVC, người bán hay người mua trả đều vậy) — phần khách trả thu qua COD riêng.
 * Tài liệu gửi khách phải cộng nó vào "Tổng tiền thanh toán", nhưng CHỈ khi
 * `fee_payer === 'buyer'`: shop trả phí thì đó là chi phí của shop, không phải
 * dòng tiền khách thanh toán.
 *
 * Dòng `service_lines` kind `shipping` của đơn khách trả là snapshot kế toán ĐÃ
 * bóc VAT (ADR-029, xem `orderToMoneyResult.ts`): 30.000 = 27.778 + 2.222. Chỉ
 * dùng nó khi hai số cộng lại đúng bằng `shipping_cost` (đơn cũ ghi VAT cộng
 * THÊM bên trên, cộng lại sẽ lệch) — còn lại hiện nguyên `shipping_cost`, VAT 0.
 *
 * TCR-2668: hoá đơn POS / hoá đơn bán hàng (`printReceipt.ts`) dùng lại hàm này
 * để dòng phí ship và tổng tiền khớp đúng file gửi khách.
 */
export function resolveBuyerShipping(order: TenantOrder): { amount: number; vat: number; discount: number } | null {
  const shipping = order.shipping;
  const cost = toNumber(shipping?.shipping_cost);
  if (shipping?.fee_payer !== 'buyer' || cost <= 0) return null;
  const discount = Math.min(cost, Math.max(0, toNumber(order.summary?.shipping_discount)));
  const snapshot = (order.service_lines ?? []).find((l) => l.kind === 'shipping');
  if (snapshot) {
    const amount = toNumber(snapshot.amount);
    const vat = toNumber(snapshot.vat_amount);
    if (Math.round(amount + vat) === Math.round(cost)) return { amount, vat, discount };
  }
  return { amount: cost, vat: 0, discount };
}

/** Dòng dịch vụ đã nằm trong `summary.total` (phụ thu...) — mọi kind trừ ship. */
function chargedServiceLines(order: TenantOrder): ServiceLine[] {
  return (order.service_lines ?? []).filter((l) => l.kind !== 'shipping' && toNumber(l.amount) > 0);
}

export function buildOrderCustomerDocData(order: TenantOrder): OrderCustomerDocData {
  const buyer = order.buyer_invoice_info;
  const address = order.shipping?.address;

  const lines: OrderCustomerDocLine[] = (order.items ?? []).map((raw) => {
    const it = raw as DocItem;
    const qty = toNumber(it.qty);
    const unitPrice = grossUnitPrice(it);
    return {
      name: it.product_name || it.name || '',
      // Sản phẩm combo thường không khai ĐVT ở master data (`unit_id: null`) —
      // hiện "Combo" thay vì ô trống; dòng thường thiếu ĐVT thì để trống, không bịa.
      unit: it.unit_name || (it.is_combo_product ? 'Combo' : ''),
      qty,
      unitPrice,
      amount: qty * unitPrice,
    };
  });

  const services = chargedServiceLines(order);
  // Đơn cũ trước khi có `service_lines`: phụ thu chỉ còn ở `surcharge_amount`.
  const legacySurcharge = (order.service_lines ?? []).length === 0 ? toNumber(order.surcharge_amount) : 0;
  for (const line of services) {
    const amount = toNumber(line.amount);
    lines.push({ name: line.service_name, unit: line.unit_name || 'Lần', qty: 1, unitPrice: amount, amount });
  }
  if (legacySurcharge > 0) {
    lines.push({ name: 'Phụ thu', unit: 'Lần', qty: 1, unitPrice: legacySurcharge, amount: legacySurcharge });
  }

  const ship = resolveBuyerShipping(order);
  if (ship) {
    lines.push({ name: 'Phí vận chuyển', unit: 'Đơn', qty: 1, unitPrice: ship.amount, amount: ship.amount });
  }

  const summary = order.summary;
  const originalPrice = toNumber(summary?.original_price);
  const finalPrice = toNumber(summary?.final_price);
  // Chiết khấu = phần `final_price` đã trừ so với giá gốc — gồm đủ CK dòng,
  // voucher, CK tích luỹ, điểm (xem `orderToMoneyResult`: đọc `final_price`,
  // KHÔNG tự cộng từng khoản vì sẽ sót/ trùng) — cộng phần freeship của phí ship.
  const goodsDiscount = summary?.final_price == null ? toNumber(order.discount) : Math.max(0, originalPrice - finalPrice);
  const discount = goodsDiscount + (ship?.discount ?? 0);
  const vat = toNumber(summary?.vat) + (ship?.vat ?? 0);
  const total = toNumber(order.total_amount) + (ship ? ship.amount + ship.vat - ship.discount : 0);

  return {
    orderNumber: order.order_number,
    orderDate: order.created_at ? formatDate(order.created_at) : '',
    customerName: buyer?.name || order.customer?.name || '',
    taxCode: buyer?.tax_id || '',
    billingAddress: buyer?.address || '',
    recipientName: address?.name || '',
    recipientPhone: address?.phone || '',
    shippingAddress: joinAddress([
      address?.address,
      address?.ward_name || order.shipping_ward_name,
      address?.city_name || order.shipping_city_name,
    ]),
    deliveryNote: address?.note?.trim() || '',
    lines,
    subtotal: lines.reduce((sum, l) => sum + l.amount, 0),
    discount,
    vat,
    total,
  };
}

/** Escape HTML — dùng chung cho bản in (tiêu đề tab) và bản docx. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const esc = escapeHtml;

const money = (n: number) => formatNumber(Math.round(n));

/** CSS của bản HTML (chụp thành Ảnh/PDF). Bản Word dựng riêng ở `orderCustomerDocx.ts`. */
const DOC_STYLE = `
  .tcr-order-doc { width: 794px; box-sizing: border-box; padding: 48px 56px; background: #fff; color: #000;
    font-family: 'Times New Roman', Times, serif; font-size: 15px; line-height: 1.35; }
  .tcr-order-doc h1 { text-align: center; font-size: 26px; margin: 0 0 28px; }
  .tcr-order-doc table { border-collapse: collapse; width: 100%; }
  .tcr-order-doc .info td { padding: 3px 0; vertical-align: top; }
  .tcr-order-doc .rule-bottom { border-bottom: 1px solid #000; }
  .tcr-order-doc .thanks { font-style: italic; margin: 18px 0 10px; }
  .tcr-order-doc .items th, .tcr-order-doc .items td { border: 1px solid #000; padding: 8px 6px; }
  .tcr-order-doc .items th { background: #e7e6e6; }
  .tcr-order-doc .c { text-align: center; } .tcr-order-doc .r { text-align: right; }
  .tcr-order-doc .totals td { padding: 4px 6px; }
  .tcr-order-doc .totals tr:first-child td { border-top: 1px solid #000; }
  .tcr-order-doc .words { margin: 12px 0 40px; }
  .tcr-order-doc .sign td { text-align: center; font-weight: bold; padding-bottom: 70px; }
  .tcr-order-doc .sign .sign-note td { font-weight: normal; padding-bottom: 0; }
`;

/** HTML một khối `<div class="tcr-order-doc">` kèm `<style>` — render ra canvas cho Ảnh/PDF. */
export function renderOrderCustomerDocHtml(data: OrderCustomerDocData): string {
  const rows = data.lines
    .map(
      (l, i) => `<tr>
        <td class="c">${i + 1}</td>
        <td class="c">${esc(l.name)}</td>
        <td class="c">${esc(l.unit)}</td>
        <td class="r">${formatNumber(l.qty)}</td>
        <td class="r">${money(l.unitPrice)}</td>
        <td class="r">${money(l.amount)}</td>
      </tr>`,
    )
    .join('');

  const totalRow = (label: string, value: number) =>
    `<tr><td style="width:45%"></td><td><b>${label}</b></td><td class="r">${money(value)}</td></tr>`;

  return `<style>${DOC_STYLE}</style>
<div class="tcr-order-doc">
  <h1>ĐƠN HÀNG</h1>
  <table class="info">
    <tr>
      <td style="width:55%"><b>Khách hàng:</b> ${esc(data.customerName)}</td>
      <td><b>Ngày đặt hàng:</b> ${esc(data.orderDate)}</td>
    </tr>
    <tr>
      <td><b>Mã số thuế:</b> ${esc(data.taxCode)}</td>
      <td><b>Số đơn hàng:</b> ${esc(data.orderNumber)}</td>
    </tr>
    <tr><td colspan="2" class="rule-bottom"><b>Địa chỉ hóa đơn:</b> ${esc(data.billingAddress)}</td></tr>
    <tr>
      <td><b>Người nhận hàng:</b> ${esc(data.recipientName)}</td>
      <td><b>SĐT người nhận:</b> ${esc(data.recipientPhone)}</td>
    </tr>
    <tr><td colspan="2"><b>Địa chỉ giao hàng:</b> ${esc(data.shippingAddress)}</td></tr>
    ${data.deliveryNote ? `<tr><td colspan="2"><b>Ghi chú giao hàng:</b> ${esc(data.deliveryNote)}</td></tr>` : ''}
  </table>
  <p class="thanks">Lời đầu tiên, xin trân trọng cảm ơn quý khách hàng đã lựa chọn sản phẩm của công ty chúng tôi. Chúng tôi xin gửi đến quý khách hàng thông tin chi tiết của đơn hàng như sau:</p>
  <table class="items">
    <thead><tr>
      <th style="width:7%">STT</th><th>TÊN SẢN PHẨM</th><th style="width:11%">ĐVT</th>
      <th style="width:9%">SL</th><th style="width:16%">ĐƠN GIÁ</th><th style="width:19%">THÀNH TIỀN</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <table class="totals">
    ${totalRow('Tổng thành tiền:', data.subtotal)}
    ${totalRow('Tiền chiết khấu:', data.discount)}
    ${totalRow('Thuế GTGT:', data.vat)}
    ${totalRow('Tổng tiền thanh toán:', data.total)}
  </table>
  <p class="words"><b><i>Số tiền viết bằng chữ:</i></b> <i>${esc(amountToVietnameseWords(data.total))}</i></p>
  <table class="sign">
    <tr><td>Người bán</td><td>Giám đốc</td></tr>
    <tr class="sign-note"><td>(Ký, họ tên)</td><td>(Ký, họ tên, đóng dấu)</td></tr>
  </table>
</div>`;
}
